import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { formatMinor } from '../../../shared/utils/money';
import { useReferralPrograms } from '../hooks/useReferrals';
import {
  REWARD_TYPE_LABELS,
  RewardAmountType,
  TRIGGER_LABELS,
  type ReferralRewardProgram,
} from '../types';
import { ProgramFormModal } from './ProgramFormModal';

const amountOf = (program: ReferralRewardProgram) =>
  program.amountType === RewardAmountType.FIXED
    ? program.amountMinor != null
      ? formatMinor(program.amountMinor)
      : '—'
    : program.percentage != null
      ? `${program.percentage}% of deal`
      : '—';

/** Reward rules — per program, with one optional platform default. */
export function ProgramsPanel({ canManage }: { canManage: boolean }) {
  const { data: programs = [], isLoading, error } = useReferralPrograms();
  const [editing, setEditing] = useState<ReferralRewardProgram | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          What a referrer earns and when. A code without its own rule uses the platform default.
        </p>
        {canManage && (
          <Button size="sm" className="h-[40px]" onClick={() => setCreating(true)}>
            New reward rule
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-red-500">Could not load reward rules.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && !error && programs.length === 0 && (
        <p className="text-sm text-muted-foreground">No reward rules yet.</p>
      )}

      {programs.length > 0 && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Reward</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Amount</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Trigger</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Status</th>
                  {canManage && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {programs.map((program) => (
                  <tr key={program.id} className="border-b border-border">
                    <td className="px-4 py-3 font-medium text-foreground">
                      {program.name}
                      {program.isDefault && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {REWARD_TYPE_LABELS[program.rewardType]}
                    </td>
                    <td className="px-4 py-3 text-foreground">{amountOf(program)}</td>
                    <td className="px-4 py-3 text-foreground">
                      {TRIGGER_LABELS[program.triggerCondition]}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          program.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {program.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-[40px]"
                          onClick={() => setEditing(program)}
                        >
                          Edit
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && <ProgramFormModal onClose={() => setCreating(false)} />}
      {editing && <ProgramFormModal program={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
